import React, { useState, useEffect } from 'react'
import { connect } from 'react-redux'

import { fetchPublicApiDataIfNeeded, callPublicApi } from '../../../actions/api'
import { apiResourceEndpoint } from '../../../constants/routes'
import { upsertForm } from '../../../reducers/form'
import { formStates } from '../../../constants/helper-states'

import InputElement from '../../utils/input'
import * as MASKS from '../../utils/input-masks'
import SHSForm from '../../utils/shs-form'
import { sortOptions } from '../../utils/option-sort'
import { toWholeNumber } from '../../utils/formatters'
import { displayDateKey } from '../../utils/date-keys'
import FormSection from './form-section'
import InquiryBackLink from './back-link'
import { OptionButtons, MultiOptionButtons } from './option-buttons'
import { DAYS_OF_WEEK_OPTIONS, MEAL_TIME_OPTIONS, DELIVERY_TYPE_OPTIONS, ALLERGY_COUNT_OPTIONS, PREFERRED_CONTACT_OPTIONS } from '../../../constants/inquiry-options'

const SECTIONS = ['schedule', 'dietary', 'about']
const ANIMATION_DURATION = 320

const INITIAL_FIELDS = {
    formType: 'weekly_meal',
    eventDate: '',
    daysOfWeek: [],
    mealTimes: [],
    headcount: '',
    deliveryType: '',
    allergies: [],
    dietaryPreferences: [],
    allergyCount: '',
    dislikes: '',
    message: '',
    firstName: '',
    lastName: '',
    phone: '',
    email: '',
    preferredContact: '',
    city: '',
    state: '',
    street: '',
    postal: '',
    referralSource: '',
    mailingList: false
}

const mapStateToProps = (state) => ({
    apiAllergens: state.apiAllergens,
    apiDietaryRestrictions: state.apiDietaryRestrictions
})

const WeeklyMealsInquiry = (props) => {
    const { dispatch, apiAllergens, apiDietaryRestrictions } = props

    const [activeSection, setActiveSection] = useState(0)
    const [completedSections, setCompletedSections] = useState([])
    const [completingSection, setCompletingSection] = useState(null)
    const [submitted, setSubmitted] = useState(false)
    const [errors, setErrors] = useState({})
    const [fields, setFields] = useState(INITIAL_FIELDS)

    useEffect(() => {
        dispatch(fetchPublicApiDataIfNeeded(apiResourceEndpoint('public/api-allergens', 'LIST')))
        dispatch(fetchPublicApiDataIfNeeded(apiResourceEndpoint('public/api-dietary-restrictions', 'LIST')))
    }, [dispatch])

    const handleChange = (field, value) => {
        setFields(prev => ({ ...prev, [field]: value }))
        if (errors[field]) {
            setErrors(prev => ({ ...prev, [field]: null }))
        }
    }

    const sectionStatus = (idx) => {
        if (idx === completingSection) {
            return 'completing'
        }
        if (completedSections.includes(idx)) {
            return 'complete'
        }
        if (activeSection === idx) {
            return 'active'
        }
        return 'locked'
    }

    const editSection = (idx) => {
        setActiveSection(idx)
        setCompletedSections(prev => prev.filter(s => s !== idx))
    }

    const advanceSection = (idx) => {
        setErrors({})
        setCompletingSection(idx)
        setTimeout(() => {
            setCompletingSection(null)
            const next = idx + 1
            setCompletedSections(prev => {
                const updated = [...prev, idx]
                return next < SECTIONS.length ? updated.filter(s => s !== next) : updated
            })
            if (next < SECTIONS.length) {
                setActiveSection(next)
                setTimeout(() => {
                    const el = document.getElementById(`section-${next}`)
                    if (el) {
                        el.scrollIntoView({ behavior: 'smooth', block: 'start' })
                    }
                }, 80)
            }
        }, ANIMATION_DURATION)
    }

    const handleSection0 = (formData, sectionFormId) => {
        dispatch(upsertForm({ id: sectionFormId, status: formStates.EMPTY }))
        const customErrors = {}
        if (!fields.daysOfWeek || fields.daysOfWeek.length === 0) {
            customErrors.daysOfWeek = 'Select at least one day'
        }
        if (!fields.mealTimes || fields.mealTimes.length === 0) {
            customErrors.mealTimes = 'Select at least one meal'
        }
        if (!fields.deliveryType) {
            customErrors.deliveryType = 'Required'
        }
        if (Object.keys(customErrors).length > 0) {
            setErrors(customErrors)
            return
        }
        advanceSection(0)
    }

    const handleSection1 = (formData, sectionFormId) => {
        dispatch(upsertForm({ id: sectionFormId, status: formStates.EMPTY }))
        const customErrors = {}
        if (!fields.allergyCount) {
            customErrors.allergyCount = 'Required'
        }
        if (Object.keys(customErrors).length > 0) {
            setErrors(customErrors)
            return
        }
        advanceSection(1)
    }

    const handleSection2 = (formData, sectionFormId) => {
        const payload = { ...fields, headcount: toWholeNumber(fields.headcount) }
        dispatch(callPublicApi(apiResourceEndpoint('public/event-forms', 'CREATE'), payload, sectionFormId))
    }

    const handleSuccess = () => {
        setSubmitted(true)
        window.scrollTo(0, 0)
    }

    const allergenOptions = sortOptions(apiAllergens).map(a => ({ value: a.id, label: a.name }))
    const dietaryOptions = sortOptions(apiDietaryRestrictions).map(d => ({ value: d.id, label: d.name }))

    const fallbackAllergenOptions = [
        { value: 'shellfish', label: 'Shellfish' },
        { value: 'fish', label: 'Fish' },
        { value: 'dairy', label: 'Dairy' },
        { value: 'egg', label: 'Egg' },
        { value: 'peanut', label: 'Peanut' },
        { value: 'tree_nut', label: 'Tree Nut' },
        { value: 'soy', label: 'Soy' },
        { value: 'wheat', label: 'Wheat' },
        { value: 'gluten', label: 'Gluten' },
        { value: 'celiac', label: 'Celiac' }
    ]

    const fallbackDietaryOptions = [
        { value: 'pescatarian', label: 'Pescatarian' },
        { value: 'vegetarian', label: 'Vegetarian' },
        { value: 'vegan', label: 'Vegan' },
        { value: 'keto', label: 'Keto' }
    ]

    const shownAllergenOptions = allergenOptions.length > 0 ? allergenOptions : fallbackAllergenOptions
    const shownDietaryOptions = dietaryOptions.length > 0 ? dietaryOptions : fallbackDietaryOptions

    const today = new Date()

    const selectedDayLabels = (fields.daysOfWeek || []).map(d => {
        const opt = DAYS_OF_WEEK_OPTIONS.find(o => o.value === d)
        return opt ? opt.label : d
    }).join(', ')

    const selectedMealLabels = (fields.mealTimes || []).map(m => {
        const opt = MEAL_TIME_OPTIONS.find(o => o.value === m)
        return opt ? opt.label : m
    }).join(', ')

    const selectedDelivery = DELIVERY_TYPE_OPTIONS.find(o => o.value === fields.deliveryType)

    const section0Summary = [
        fields.eventDate ? `Starting ${displayDateKey(fields.eventDate)}` : null,
        selectedDayLabels || null,
        selectedMealLabels || null,
        selectedDelivery ? selectedDelivery.label : null,
        toWholeNumber(fields.headcount) ? `${toWholeNumber(fields.headcount).toLocaleString()} portions` : null
    ].filter(Boolean).join(' · ')

    const section1Summary = [
        fields.allergies && fields.allergies.length > 0
            ? shownAllergenOptions.filter(a => fields.allergies.includes(a.value)).map(a => a.label).join(', ')
            : 'No allergies noted',
        fields.dislikes ? `Dislikes: ${fields.dislikes}` : null
    ].filter(Boolean).join(' · ')

    const section2Summary = [
        [fields.firstName, fields.lastName].filter(Boolean).join(' '),
        fields.email
    ].filter(Boolean).join(' · ')

    if (submitted) {
        return (
            <>
                <div className="full-page-bg-img" style={{ backgroundImage: 'url("/img/bg/place-setting.jpg")' }} />
                <div className="form-holder">
                    <div className="event-form">
                        <div className="form-header">
                            <img src="/img/icons/tivity-logo.png" alt="Tivity" width="120px" />
                            <h1>{`You're all set!`}</h1>
                            <p>Thank you for your inquiry. Someone from the Tivity team will reach out within 24 hours with options tailored to your schedule.</p>
                        </div>
                    </div>
                </div>
            </>
        )
    }

    return (
        <>
            <div className="full-page-bg-img" style={{ backgroundImage: 'url("/img/bg/place-setting.jpg")' }} />
            <div className="form-holder inquiry-form-holder">
                <div className="event-form inquiry-form">
                    <InquiryBackLink />
                    <div className="form-header">
                        <img src="/img/icons/tivity-logo.png" alt="Tivity" width="120px" />
                        <h1>Weekly Chef Services</h1>
                        <p>Custom weekly Chef prepared meals in your home or delivered to you. Tell us what you need and we will reach back out within 24 hours.</p>
                    </div>

                    <div id="section-0">
                        <FormSection
                            number={1}
                            title="Your Schedule"
                            status={sectionStatus(0)}
                            summary={section0Summary}
                            onEdit={() => editSection(0)}>
                            <SHSForm
                                submitFX={handleSection0}
                                successFX={() => {}}
                                buttonLabel="Continue to Dietary &rarr;"
                                formClass="inquiry-section__continue flex-100">
                                <InputElement
                                    type="text"
                                    id="firstName"
                                    label="First Name"
                                    default={fields.firstName}
                                    onChange={handleChange}
                                    placeholder="First Name"
                                    extraClass="flex-50"
                                    required />
                                <InputElement
                                    type="text"
                                    id="lastName"
                                    label="Last Name"
                                    default={fields.lastName}
                                    onChange={handleChange}
                                    placeholder="Last Name"
                                    extraClass="flex-50"
                                    required />
                                <InputElement
                                    type="tel"
                                    id="phone"
                                    label="Cell Phone"
                                    default={fields.phone}
                                    onChange={handleChange}
                                    isFormatted={true}
                                    formatMask={MASKS.phoneMask}
                                    placeholder="(555) 000-0000"
                                    extraClass="flex-50"
                                    required />
                                <InputElement
                                    type="email"
                                    id="email"
                                    label="Email"
                                    default={fields.email}
                                    onChange={handleChange}
                                    placeholder="your@email.com"
                                    extraClass="flex-50"
                                    required />
                                <InputElement
                                    type="datePicker"
                                    dateType="date"
                                    id="eventDate"
                                    label="Requested Start Date"
                                    default={fields.eventDate}
                                    onChange={handleChange}
                                    options={{
                                        minDate: today,
                                        maxDate: new Date(today.getFullYear() + 1, today.getMonth(), today.getDate()),
                                        placeholderText: 'When would you like to start?'
                                    }}
                                    extraClass="flex-100"
                                    required />
                                <MultiOptionButtons
                                    id="daysOfWeek"
                                    label="Which days of the week?"
                                    error={errors.daysOfWeek}
                                    options={DAYS_OF_WEEK_OPTIONS}
                                    value={fields.daysOfWeek}
                                    onChange={handleChange}
                                    required />
                                <MultiOptionButtons
                                    id="mealTimes"
                                    label="Which meals?"
                                    error={errors.mealTimes}
                                    options={MEAL_TIME_OPTIONS}
                                    value={fields.mealTimes}
                                    onChange={handleChange}
                                    required />
                                <InputElement
                                    type="text"
                                    id="headcount"
                                    label="How many portions per meal?"
                                    default={fields.headcount}
                                    onChange={handleChange}
                                    placeholder="e.g. 4"
                                    isFormatted={true}
                                    formatMask={MASKS.countMask}
                                    extraClass="flex-100"
                                    required />
                                <InputElement
                                    type="text"
                                    id="city"
                                    label="City"
                                    default={fields.city}
                                    onChange={handleChange}
                                    placeholder="City"
                                    extraClass="flex-50"
                                    required />
                                <InputElement
                                    type="text"
                                    id="state"
                                    label="State"
                                    default={fields.state}
                                    onChange={handleChange}
                                    placeholder="State"
                                    extraClass="flex-50"
                                    required />
                                <InputElement
                                    type="mapboxAddress"
                                    id="street"
                                    label="Street Address"
                                    default={fields.street}
                                    onChange={handleChange}
                                    placeholder="Street address"
                                    autoComplete="street-address"
                                    extraClass="flex-50" />
                                <InputElement
                                    type="text"
                                    id="postal"
                                    label="Zip Code"
                                    default={fields.postal}
                                    onChange={handleChange}
                                    placeholder="Zip code"
                                    extraClass="flex-50"
                                    required />
                                <OptionButtons
                                    id="deliveryType"
                                    label="Which service are you requesting?"
                                    error={errors.deliveryType}
                                    options={DELIVERY_TYPE_OPTIONS}
                                    value={fields.deliveryType}
                                    onChange={handleChange}
                                    required />
                            </SHSForm>
                        </FormSection>
                    </div>

                    <div id="section-1">
                        <FormSection
                            number={2}
                            title="Dietary & Details"
                            status={sectionStatus(1)}
                            summary={section1Summary}
                            onEdit={() => editSection(1)}>
                            <SHSForm
                                submitFX={handleSection1}
                                successFX={() => {}}
                                buttonLabel="Continue to About You &rarr;"
                                formClass="inquiry-section__continue flex-100">
                                <MultiOptionButtons
                                    id="allergies"
                                    label="Allergies (select all that apply)"
                                    options={shownAllergenOptions}
                                    value={fields.allergies}
                                    onChange={handleChange} />
                                <MultiOptionButtons
                                    id="dietaryPreferences"
                                    label="Dietary Preferences"
                                    options={shownDietaryOptions}
                                    value={fields.dietaryPreferences}
                                    onChange={handleChange} />
                                <OptionButtons
                                    id="allergyCount"
                                    label="How many people have an allergy or dietary restriction?"
                                    error={errors.allergyCount}
                                    options={ALLERGY_COUNT_OPTIONS}
                                    value={fields.allergyCount}
                                    onChange={handleChange}
                                    required />
                                <InputElement
                                    type="textarea"
                                    id="dislikes"
                                    label="List anything you or your household do NOT like to eat"
                                    default={fields.dislikes}
                                    onChange={handleChange}
                                    placeholder="e.g. No mushrooms, hate cilantro, picky eaters in the household..."
                                    extraClass="flex-100" />
                                <InputElement
                                    type="textarea"
                                    id="message"
                                    label="Any other special requests?"
                                    default={fields.message}
                                    onChange={handleChange}
                                    placeholder="Anything else we should know..."
                                    extraClass="flex-100" />
                            </SHSForm>
                        </FormSection>
                    </div>

                    <div id="section-2">
                        <FormSection
                            number={3}
                            title="About you"
                            status={sectionStatus(2)}
                            summary={section2Summary}
                            onEdit={() => editSection(2)}>
                            <SHSForm
                                submitFX={handleSection2}
                                successFX={handleSuccess}
                                buttonLabel="Submit Inquiry"
                                formClass="inquiry-section__continue flex-100">
                                <OptionButtons
                                    id="preferredContact"
                                    label="Preferred contact method"
                                    options={PREFERRED_CONTACT_OPTIONS}
                                    value={fields.preferredContact}
                                    onChange={handleChange} />
                                <InputElement
                                    type="text"
                                    id="referralSource"
                                    label="How did you hear about Tivity?"
                                    default={fields.referralSource}
                                    onChange={handleChange}
                                    placeholder="e.g. Instagram, a friend, Google..."
                                    extraClass="flex-100" />
                            </SHSForm>
                        </FormSection>
                    </div>
                </div>
            </div>
        </>
    )
}

export default connect(mapStateToProps)(WeeklyMealsInquiry)
